import type { Zone } from '@seek/audience-zones';
import {
  getZoneFromSite,
  type SiteNamesMatchingClassification,
} from '@seek/melways-sites';

import { CONFIG } from './modules/window';

export type Environment = 'development' | 'staging' | 'production';

export type Site = Exclude<
  SiteNamesMatchingClassification<{
    product: 'candidate';
  }>,
  'candidate-seek-root' | 'candidate-jobsdb-root' | 'candidate-jobstreet-root'
>;

export interface Config {
  USE_ANALYTICS: boolean;
  DATADOG_BROWSER_LOG_ENABLED: boolean;
  DATADOG_RUM_ENABLED: boolean;
  ENV: string;
  ANALYTICS_ENV: string;
  ANALYTICS_PROFILE: string;
  CHANGE_EMAIL_URL: string;
  METRICS_ENABLED: boolean;
  METRICS_URL: string;
  site: Site;
  version: string;
  zone: Zone;
  CANDIDATE_GRAPH_DARK_PROD_URL: string;
}

const configs: Record<
  Environment,
  Omit<Config, 'version' | 'site' | 'zone'>
> = {
  development: {
    USE_ANALYTICS: false,
    DATADOG_BROWSER_LOG_ENABLED: false,
    DATADOG_RUM_ENABLED: false,
    ENV: 'local',
    ANALYTICS_ENV: 'dev',
    ANALYTICS_PROFILE: 'candidate-main',
    CHANGE_EMAIL_URL: 'http://stub.seek.com.au:3002/changeEmailAddress',
    METRICS_ENABLED: false,
    METRICS_URL: '',
    CANDIDATE_GRAPH_DARK_PROD_URL: '',
  },

  staging: {
    USE_ANALYTICS: true,
    DATADOG_BROWSER_LOG_ENABLED: false,
    DATADOG_RUM_ENABLED: false,
    ENV: 'dev',
    ANALYTICS_ENV: 'dev',
    ANALYTICS_PROFILE: 'candidate-main',
    CHANGE_EMAIL_URL: '/MyAccount/ChangeEmailAddress?comeFrom=cf_null',
    METRICS_ENABLED: true,
    METRICS_URL: 'https://seek-metrics-forwarder.staging.cloud.seek.com.au',
    CANDIDATE_GRAPH_DARK_PROD_URL: '',
  },

  production: {
    USE_ANALYTICS: true,
    DATADOG_BROWSER_LOG_ENABLED: true,
    DATADOG_RUM_ENABLED: true,
    ENV: 'production',
    ANALYTICS_ENV: 'prod',
    ANALYTICS_PROFILE: 'candidate-main',
    CHANGE_EMAIL_URL: '/MyAccount/ChangeEmailAddress?comeFrom=cf_null',
    METRICS_ENABLED: true,
    METRICS_URL: 'https://seek-metrics-forwarder.cloud.seek.com.au',
    CANDIDATE_GRAPH_DARK_PROD_URL:
      'https://candidate-graphql-dark-prod.cloud.seek.com.au/graphql',
  },
};

const validate = ({
  config: currentConfig,
  site,
  environment,
}: {
  config: Config;
  site: Site;
  environment: Environment;
}) => {
  const EXPECTED_CONFIG = [
    'USE_ANALYTICS',
    'DATADOG_BROWSER_LOG_ENABLED',
    'DATADOG_RUM_ENABLED',
    'ENV',
    'ANALYTICS_ENV',
    'ANALYTICS_PROFILE',
    'CHANGE_EMAIL_URL',
    'METRICS_ENABLED',
    'METRICS_URL',
  ];

  const missingConfig = EXPECTED_CONFIG.reduce((acc, property) => {
    if (!currentConfig.hasOwnProperty(property)) {
      return acc.concat(property);
    }

    return acc;
  }, [] as string[]);

  if (missingConfig.length) {
    throw new Error(
      `Site:[${site}] - Environment:[${environment}] \n
       Missing the following configuration: ${JSON.stringify(missingConfig)}`,
    );
  }

  return currentConfig;
};

export const getConfig = ({
  site,
  environment,
}: {
  site: Site;
  environment: Environment;
}): Config => {
  const version = process?.env?.BUILD_NUMBER || 'local';
  const zone = getZoneFromSite(site);
  const current = { ...configs[environment], site, version, zone };

  return validate({ config: current, site, environment });
};

export const CANDIDATE_GRAPH_DARK_PROD_URL =
  CONFIG?.CANDIDATE_GRAPH_DARK_PROD_URL ?? '';
